import React, { useEffect, useRef } from 'react';

const MAX_TRAIL = 22;
const TRAIL_DECAY = 0.045;
const CURSOR_GLOW = 32;
const TRAIL_GLOW = 22;

function parseColorToRgb(color) {
  const trimmed = color.trim();
  if (trimmed.startsWith('#')) {
    const hex = trimmed.slice(1);
    const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
    const n = parseInt(full, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }
  const match = trimmed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (match) {
    return { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]) };
  }
  return { r: 193, g: 76, b: 26 };
}

function drawGlow(ctx, x, y, radius, rgb, alpha) {
  if (alpha <= 0.01 || radius <= 0) return;
  const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
  gradient.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`);
  gradient.addColorStop(0.35, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha * 0.45})`);
  gradient.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`);
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fill();
}

export function AuthBrandGridGlow({ containerRef }) {
  const canvasRef = useRef(null);
  const trailRef = useRef([]);
  const mouseActiveRef = useRef(false);
  const rafRef = useRef(null);
  const colorsRef = useRef({
    core: { r: 232, g: 135, b: 74 },
    mid: { r: 154, g: 92, b: 42 },
  });

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return undefined;
    if (process.env.NODE_ENV === 'test') return undefined;

    const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (motionQuery?.matches) return undefined;

    let ctx;
    try {
      ctx = canvas.getContext('2d');
    } catch {
      return undefined;
    }
    if (!ctx) return undefined;

    const readColors = () => {
      const root = getComputedStyle(document.documentElement);
      const core = root.getPropertyValue('--y-rust').trim() || '#B4501C';
      const mid = root.getPropertyValue('--y-rust-dark').trim() || '#8E3C12';
      colorsRef.current = {
        core: parseColorToRgb(core),
        mid: parseColorToRgb(mid),
      };
    };

    const resize = () => {
      readColors();
      const rect = container.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const ro = new ResizeObserver(resize);
    ro.observe(container);
    resize();

    const paint = () => {
      const rect = container.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);

      const trail = trailRef.current;
      for (let i = trail.length - 1; i >= 0; i -= 1) {
        trail[i].life -= TRAIL_DECAY;
        if (trail[i].life <= 0) trail.splice(i, 1);
      }

      trail.forEach((point, index) => {
        const trailFade = point.life * (0.25 + (index / Math.max(trail.length, 1)) * 0.35);
        drawGlow(ctx, point.x, point.y, TRAIL_GLOW * point.life, colorsRef.current.mid, trailFade);
      });

      if (trail.length > 0) {
        const head = trail[trail.length - 1];
        drawGlow(ctx, head.x, head.y, CURSOR_GLOW, colorsRef.current.core, 0.75 * head.life);
        ctx.fillStyle = `rgba(${colorsRef.current.core.r}, ${colorsRef.current.core.g}, ${colorsRef.current.core.b}, ${0.95 * head.life})`;
        ctx.beginPath();
        ctx.arc(head.x, head.y, 3.5, 0, Math.PI * 2);
        ctx.fill();
      }

      if (trail.length > 0 || mouseActiveRef.current) {
        rafRef.current = requestAnimationFrame(paint);
      } else {
        rafRef.current = null;
      }
    };

    const schedulePaint = () => {
      if (!rafRef.current) rafRef.current = requestAnimationFrame(paint);
    };

    const onMove = (event) => {
      const rect = container.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      mouseActiveRef.current = true;
      trailRef.current.push({ x, y, life: 1 });
      if (trailRef.current.length > MAX_TRAIL) trailRef.current.shift();
      schedulePaint();
    };

    const onLeave = () => {
      mouseActiveRef.current = false;
      schedulePaint();
    };

    container.addEventListener('mousemove', onMove);
    container.addEventListener('mouseleave', onLeave);

    return () => {
      container.removeEventListener('mousemove', onMove);
      container.removeEventListener('mouseleave', onLeave);
      ro.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [containerRef]);

  return <canvas className="auth-brand__grid-glow" ref={canvasRef} aria-hidden="true" />;
}
